import { XAxisOptions, YAxisOptions } from "highcharts";
import { useMemo } from "react";

import { useSelectedTimeRange } from "@/features/dashboard/store";

import type { ChartTimeBounds } from "./get-shared-chart-time-bounds";

// Y-axis IDs for referencing axes by name instead of index
export const Y_AXIS_ID = {
  PRIMARY: "primary-y-axis",
} as const;

const baseOptions: Partial<Highcharts.Options> = {
  title: {
    align: "left",
    style: {
      fontSize: "1em",
    },
  },
  tooltip: {
    xDateFormat: "%A, %d-%m-%Y %H:%M",
  },
  colors: ["#8FCAE7", "#01689B", "#CCE0F1"],
  legend: {
    padding: 0,
    navigation: {
      activeColor: "#CCE0F1",
    },
  },
  chart: {
    height: 400,
    marginLeft: 65,
    marginRight: 35,
  },
};

const useGraphOptions = (
  options?: Partial<Highcharts.Options>,
  timeBounds?: ChartTimeBounds,
): Highcharts.Options => {
  const timeRange = useSelectedTimeRange();

  const title = useMemo(() => {
    return {
      ...baseOptions.title,
      ...options?.title,
    };
  }, [options]);

  const series = options?.series ?? [];

  // merge base and configuration xAxis
  const xAxis = useMemo(() => {
    const newAxis: XAxisOptions[] = [
      {
        type: "datetime",
        min: timeBounds?.min ?? +timeRange.timeStart,
        max: timeBounds?.max ?? +timeRange.timeEnd,
        startOfWeek: 0,
        gridLineWidth: 1,
        gridZIndex: 0,
      },
    ];

    if (options?.xAxis) {
      if (Array.isArray(options.xAxis)) {
        options.xAxis.forEach((axis, index) => {
          newAxis[index] = {
            ...newAxis[index],
            ...axis,
          };
        });
      } else {
        newAxis[0] = {
          ...newAxis[0],
          ...options.xAxis,
        };
      }
    }
    return newAxis;
  }, [options, timeRange, timeBounds]);

  // merge base and configured yAxis
  const yAxis = useMemo(() => {
    const newAxis: YAxisOptions[] = [
      {
        id: Y_AXIS_ID.PRIMARY,
        gridZIndex: 0,
        labels: {
          reserveSpace: true,
        },
      },
    ];

    if (options?.yAxis) {
      if (Array.isArray(options.yAxis)) {
        options.yAxis.forEach((axis, index) => {
          newAxis[index] = {
            ...newAxis[index],
            ...axis,
          };
        });
      } else {
        newAxis[0] = {
          ...newAxis[0],
          ...options.yAxis,
        };
      }
    }

    return newAxis;
  }, [options]);

  const chart = useMemo<Highcharts.ChartOptions>(() => {
    const callerChart = options?.chart ?? {};
    const useFixedChartHeight = typeof callerChart.height === "number";
    const { render: callerRender, ...callerChartEventsRest } = callerChart.events ?? {};

    return {
      ...baseOptions.chart,
      ...callerChart,
      events: {
        ...callerChartEventsRest,
        render: function (this: Highcharts.Chart, event: Event) {
          callerRender?.call(this, event);

          if (!useFixedChartHeight) {
            const chartHeight = this.chartHeight;
            const legendHeight = this.legend.box.getBBox().height;
            const targetHeight = 390 + legendHeight;
            if (chartHeight !== targetHeight) {
              this.setSize(null, targetHeight, {
                duration: 0,
              });
            }
          }
        },
      },
    };
  }, [options]);

  const graphOptions = useMemo(
    () => ({
      ...baseOptions,
      ...options,

      title,
      xAxis,
      yAxis,
      series,
      chart,
    }),
    [options, series, xAxis, yAxis, chart],
  );

  return graphOptions;
};

export default useGraphOptions;
